import type { Criterion, CriterionOption, SnippetNode, Task } from '../../domain/types';

/**
 * Fachinhalte der Beispielstation „Herzneurose" — wörtlich aus der Design-Vorlage
 * (`design/OSCE App.dc.html`). Solange Relay die Fachaktionen nicht anbietet, speist sich
 * der Bogen hieraus; die Struktur ist dieselbe, die der Server später liefert.
 */

const CHECK = [
  'fragt nach dem Grund des Kommens, arbeitet das Beschwerdebild heraus',
  'fragt nach den Begleitsymptomen',
  'erhebt eine Familienanamnese',
  'erhebt eine Sozialanamnese',
  'fragt nach chron. Erkrankungen, KH-Aufenthalten, Medikamenteneinnahme / Drogen',
];

// Beste Stufe zuerst, wie in Relay- und Classic-Paketen (siehe `scaleStep`).
const SCALE4: CriterionOption[] = [
  { label: 'vollständig gezeigt', points: 3 },
  { label: 'größtenteils gezeigt', points: 2 },
  { label: 'ansatzweise gezeigt', points: 1 },
  { label: 'nicht gezeigt', points: 0 },
];

/**
 * Zehnstufige Zählskala. Die Abstände sind gleichmäßig — Skalen sind in Relay linear, die
 * Punkte einer Stufe ergeben sich aus Position und Höchstwert (siehe `scaleStep`).
 */
const COUNT10: CriterionOption[] = [
  { label: '9 oder mehr Fragen genannt', points: 5 },
  { label: '8 Fragen, davon eine unpassend', points: 40 / 9 },
  { label: '7 Fragen genannt', points: 35 / 9 },
  { label: '6 Fragen genannt', points: 30 / 9 },
  { label: '5 Fragen genannt — Soll erreicht', points: 25 / 9 },
  { label: '4 Fragen genannt', points: 20 / 9 },
  { label: '3 Fragen genannt', points: 15 / 9 },
  { label: '2 Fragen genannt', points: 10 / 9 },
  { label: '1 Frage genannt', points: 5 / 9 },
  { label: 'keine spezifische Frage genannt', points: 0 },
];

/** Fünfstufige Erfüllungsskala mit frei wählbarer Höchstpunktzahl. */
const gradedScale = (max: number): CriterionOption[] => [
  { label: 'vollständig erfüllt', points: max },
  { label: 'überwiegend erfüllt', points: max * 0.75 },
  { label: 'teilweise erfüllt', points: max * 0.5 },
  { label: 'ansatzweise erfüllt', points: max * 0.25 },
  { label: 'nicht erfüllt', points: 0 },
];

const CRIT_ANAMNESE: Criterion[] = [
  { label: 'Anzahl und Qualität der genannten Anamnesefragen', options: COUNT10 },
  {
    label:
      'Stellt sich namentlich vor, schafft eine entspannte Atmosphäre, hält Blickkontakt',
    options: SCALE4,
  },
  {
    label:
      'Lässt den Patienten erzählen, passt Sprache an, achtet die Gefühle des Patienten',
    options: SCALE4,
  },
];

const CRIT_ABSCHLUSS: Criterion[] = [
  { label: 'Fasst die Befunde patientengerecht zusammen', options: gradedScale(40) },
  { label: 'Vereinbart konkrete nächste Schritte', options: gradedScale(40) },
  {
    label: 'Ermutigt zu Rückfragen und beendet das Gespräch klar',
    options: gradedScale(20),
  },
];

const BODY_GESPRAECHSEROEFFNUNG =
  '..stellt sich namentlich vor, sorgt für eine entspannte Atmosphäre, ..hält Blickkontakt, ' +
  '..lässt den Pat. erst einmal erzählen (offene Fragen), ..passt seine Sprache an den ' +
  'Bildungsstand des Patienten an, ..achtet die Gefühle des Patienten, ..klärt, dass nichts ' +
  'Wichtiges vergessen wurde, ..fasst Informationen themengerecht zusammen, ..ermutigt zu Fragen';

export const TASKS: Task[] = [
  {
    id: 't1',
    typeLabel: 'Globale Bewertung',
    title: 'Gesprächseröffnung und Beziehungsaufbau',
    short: 'Gesprächseröffnung',
    scoring: { kind: 'global', max: 3, body: BODY_GESPRAECHSEROEFFNUNG },
  },
  {
    id: 't2',
    typeLabel: 'Checkliste, summiert',
    title: 'Informationsgewinn der Anamnese',
    short: 'Informationsgewinn',
    scoring: { kind: 'checklist', items: CHECK.map((text) => ({ text, points: 1 })) },
  },
  {
    id: 't3',
    typeLabel: 'Bewertete Kriterien',
    title:
      'Nennen Sie mindestens 5 spezifische Anamnesefragen, um den Verdacht einer psychosomatischen Erkrankung zu erhärten',
    short: 'Spezifische Anamnesefragen',
    scoring: { kind: 'criteria', criteria: CRIT_ANAMNESE },
  },
  {
    id: 't4',
    typeLabel: 'Bewertete Kriterien',
    title: 'Gesprächsabschluss und weiteres Vorgehen',
    short: 'Gesprächsabschluss',
    scoring: { kind: 'criteria', criteria: CRIT_ABSCHLUSS },
  },
];

/**
 * Eine **lange** Station, für den Fall, den die vier Aufgaben oben nicht abbilden.
 *
 * Der Bogenabzug beim Absenden kachelt: passt der Bogen nicht auf einen Schirm, entstehen
 * mehrere Aufnahmen (`features/bogen/tiles.ts`). Mit vier Aufgaben sind das zwei Kacheln —
 * damit ist der mehrteilige Fall zwar berührt, aber nicht ausgereizt. Eine Station mit
 * fünfzehn Aufgaben ergibt ein knappes Dutzend, und erst daran zeigt sich, ob Zeitbedarf und
 * Speicher tragen.
 *
 * Sie ist bewusst Teil der Beispieldaten und kein Testgerüst: sie soll sich auch von Hand
 * durchklicken lassen.
 */
export const LANGE_TASKS: Task[] = Array.from({ length: 15 }, (_, i) => {
  const n = i + 1;
  const art = i % 3;
  if (art === 0) {
    return {
      id: 'lang-' + n,
      typeLabel: 'Checkliste, summiert',
      title: n + '. Systematische Untersuchung — Abschnitt ' + n,
      short: 'Abschnitt ' + n,
      scoring: {
        kind: 'checklist' as const,
        items: CHECK.map((text) => ({ text: text + ' (' + n + ')', points: 1 })),
      },
    };
  }
  if (art === 1) {
    return {
      id: 'lang-' + n,
      typeLabel: 'Bewertete Kriterien',
      title: n + '. Befundung und Einordnung — Abschnitt ' + n,
      short: 'Befundung ' + n,
      scoring: { kind: 'criteria' as const, criteria: CRIT_ANAMNESE },
    };
  }
  return {
    id: 'lang-' + n,
    typeLabel: 'Globale Bewertung',
    title: n + '. Gesprächsführung im Abschnitt ' + n,
    short: 'Gesprächsführung ' + n,
    scoring: { kind: 'global' as const, max: 3, body: BODY_GESPRAECHSEROEFFNUNG },
  };
});

/** Baustein-Baum des Kommentar-Reiters „Text & Bausteine". */
export const SNIPPET_TREE: SnippetNode[] = [
  {
    label: 'Gesprächsführung',
    children: [
      {
        label: 'Struktur',
        children: [
          {
            label: 'Roter Faden',
            children: [
              { text: 'Gespräch klar strukturiert, Übergänge angekündigt' },
              { text: 'Struktur teilweise unklar, Themensprünge' },
              { text: 'Kein erkennbarer roter Faden' },
            ],
          },
          {
            label: 'Zeitführung',
            children: [
              { text: 'Zeit gut eingeteilt, alle Themen abgedeckt' },
              { text: 'Zeit überzogen, Abschluss abrupt' },
            ],
          },
        ],
      },
      {
        label: 'Empathie',
        children: [
          { text: 'Gefühle des Patienten aktiv aufgegriffen' },
          { text: 'Empathische Reaktion nur ansatzweise' },
          { text: 'Emotionale Signale überhört' },
        ],
      },
      {
        label: 'Sprache',
        children: [
          { text: 'Fachbegriffe konsequent erklärt' },
          { text: 'Vereinzelt Fachjargon ohne Erläuterung' },
        ],
      },
    ],
  },
  {
    label: 'Anamnese',
    children: [
      {
        label: 'Vollständigkeit',
        children: [
          { text: 'Alle Leitsymptome systematisch erfragt' },
          { text: 'Sozialanamnese fehlt weitgehend' },
          { text: 'Medikamentenanamnese nicht erhoben' },
        ],
      },
      {
        label: 'Psychosomatik',
        children: [
          { text: 'Zusammenhang Belastung / Symptom herausgearbeitet' },
          { text: 'Auslösesituation nicht vertieft' },
        ],
      },
    ],
  },
  { label: 'Hygiene beachtet' },
  {
    label: 'Untersuchung',
    children: [
      { text: 'Untersuchungsschritte angekündigt' },
      { text: 'Reihenfolge unsystematisch' },
    ],
  },
  {
    label: 'Abschluss',
    children: [
      { text: 'Befunde patientengerecht zusammengefasst' },
      { text: 'Nächste Schritte konkret vereinbart' },
      { text: 'Weiteres Vorgehen offen gelassen' },
    ],
  },
];
